<template>
  <div
    class="short-link-tool"
    :class="{ 'short-link-tool--locked': isGenerating }"
  >
    <!-- 批量生成全页遮罩 -->
    <div v-if="isGenerating" class="batch-overlay">
      <div class="batch-overlay__panel">
        <el-icon class="batch-overlay__icon is-loading"><Loading /></el-icon>
        <p class="batch-overlay__title">正在批量生成短链</p>
        <p class="batch-overlay__channel">
          当前渠道：<strong>{{ CHANNEL_LABEL[channel] }}</strong>
        </p>
        <p class="batch-overlay__msg">{{ progressMessage }}</p>
        <el-progress
          :percentage="progressPercent"
          :stroke-width="18"
          striped
          striped-flow
          :duration="10"
        />
        <p class="batch-overlay__count">{{ progressDone }} / {{ progressTotal }}（{{ progressPercent }}%）</p>
        <p class="batch-overlay__tip">生成过程中请勿操作页面</p>
      </div>
    </div>

    <header class="tool-header">
      <div class="tool-header__main">
        <h1 class="tool-header__title">短链转换</h1>
        <p class="tool-header__desc">
          支持淘宝、京东渠道；淘宝名称最多 16 字（提交时自动追加随机字母后缀）；过期时间为生成日起 180 天
        </p>
      </div>

      <div class="channel-panel" :class="`channel-panel--${channel}`">
        <div class="channel-panel__label">
          <span class="channel-panel__required">*</span>
          短链渠道（批量前请务必确认）
        </div>
        <el-radio-group
          v-model="channel"
          size="large"
          class="channel-panel__radios"
          :disabled="isGenerating"
          @change="onChannelManualChange"
        >
          <el-radio-button label="jd">京东</el-radio-button>
          <el-radio-button label="taobao" :disabled="!taobaoSessionConfigured">淘宝</el-radio-button>
        </el-radio-group>
        <div class="channel-panel__status">
          <span class="status-label">淘宝 Session</span>
          <el-tag
            :type="sessionTagType"
            effect="dark"
            class="session-status-tag"
          >
            {{ sessionTagLabel }}
          </el-tag>
          <span class="status-action">
            <el-button
              v-if="channel === 'taobao' && taobaoSessionConfigured"
              link
              type="primary"
              size="small"
              class="status-recheck-btn"
              :loading="taobaoSessionStatus === 'checking'"
              :disabled="isGenerating"
              @click="runTaobaoPrecheck()"
            >
              重新检测
            </el-button>
          </span>
        </div>
      </div>
    </header>

    <el-alert
      v-if="channel === 'taobao' && taobaoSessionStatus === 'invalid'"
      type="error"
      :closable="true"
      show-icon
      class="session-alert"
    >
      <template #title>
        <span>{{ taobaoSessionStatusDetail }}</span>
        <el-button v-if="taobaoLastErrorPayload" link type="primary" class="error-detail-btn" @click="openTaobaoErrorDetail">
          查看详情
        </el-button>
      </template>
    </el-alert>
    <el-alert
      v-else-if="channel === 'taobao' && !taobaoSessionConfigured"
      type="error"
      :closable="true"
      show-icon
      title="淘宝 session 获取失败，请联系管理员处理"
      class="session-alert"
    />
    <section class="toolbar-card">
      <div class="toolbar-actions">
        <el-button type="primary" :icon="Download" @click="downloadTemplate">下载模板</el-button>
        <el-upload
          action="#"
          :auto-upload="false"
          :show-file-list="false"
          accept=".xlsx,.xls"
          :on-change="handleFileUpload"
          :disabled="isGenerating"
        >
          <el-button type="success">导入 Excel</el-button>
        </el-upload>
        <el-button type="warning" :disabled="isGenerating" @click="openSingleDialog">单个生成</el-button>
      </div>
      <p class="toolbar-hint">导入后可手动选择「名称列」「链接列」；链接含 mini-app-static.jd.com 自动识别为京东，含 m.duanqu.com 自动识别为淘宝</p>
    </section>

    <!-- 列映射（有预览数据时显示） -->
    <section v-if="activeSheetNames.length && columnMap[currentPreviewSheet]" class="mapping-card">
      <div class="mapping-row">
        <span class="mapping-label">当前 Sheet：{{ currentPreviewSheet }}</span>
        <span class="mapping-label">名称列</span>
        <el-select
          v-model="columnMap[currentPreviewSheet].nameKey"
          style="width: 180px"
          :disabled="isGenerating"
          @change="rebuildCurrentSheet"
        >
          <el-option v-for="h in sheetHeaders[currentPreviewSheet]" :key="'n-' + h" :label="h" :value="h" />
        </el-select>
        <span class="mapping-label">链接列</span>
        <el-select
          v-model="columnMap[currentPreviewSheet].urlKey"
          style="width: 180px"
          :disabled="isGenerating"
          @change="rebuildCurrentSheet"
        >
          <el-option v-for="h in sheetHeaders[currentPreviewSheet]" :key="'u-' + h" :label="h" :value="h" />
        </el-select>
      </div>
    </section>

    <!-- 预览 -->
    <section v-if="activeSheetNames.length" class="preview-card">
      <div class="preview-head">
        <h3>预览与编辑</h3>
        <span class="row-count">共 {{ totalRows }} 条</span>
      </div>

      <div class="sheet-tabs">
        <button
          v-for="name in activeSheetNames"
          :key="name"
          type="button"
          :class="['sheet-tab', { active: currentPreviewSheet === name }]"
          @click="currentPreviewSheet = name"
        >
          {{ name }}
          <span class="sheet-tab__count">{{ (previewDataMap[name] || []).length }}</span>
        </button>
      </div>

      <el-table :data="previewDataMap[currentPreviewSheet]" max-height="420" border stripe>
        <el-table-column label="链接名称" min-width="160">
          <template #default="{ row }">
            <el-input v-model="row.name" :disabled="isGenerating" @change="validateAll" />
          </template>
        </el-table-column>
        <el-table-column label="长链接" min-width="280">
          <template #default="{ row }">
            <el-input
              v-model="row.longUrl"
              type="textarea"
              :rows="2"
              :disabled="isGenerating"
              @change="onLongUrlChange(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="短链接" min-width="200">
          <template #default="{ row }">
            <span v-if="row.shortUrl" class="short-url">{{ row.shortUrl }}</span>
            <span v-else class="status-muted">{{ row.status }}</span>
          </template>
        </el-table-column>
        <el-table-column label="过期时间" width="170" prop="expirationDate" />
        <el-table-column label="操作" width="90" align="center">
          <template #default="{ $index }">
            <el-button type="danger" link :disabled="isGenerating" @click="removeRow($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-alert v-if="validationErrors.length" type="error" :closable="false" class="error-alert" title="校验未通过">
        <ul class="error-list">
          <li v-for="(e, i) in validationErrors.slice(0, 8)" :key="i">{{ e }}</li>
          <li v-if="validationErrors.length > 8">…还有 {{ validationErrors.length - 8 }} 条</li>
        </ul>
      </el-alert>

      <div class="preview-actions">
        <el-button
          type="success"
          :loading="isGenerating"
          :disabled="validationErrors.length > 0 || totalRows === 0 || pageTaobaoBlocked"
          @click="batchGenerate"
        >
          {{ isGenerating ? '生成中…' : '批量生成短链' }}
        </el-button>
        <el-button type="primary" :disabled="!hasGeneratedLinks || totalRows === 0" @click="downloadResult">
          下载结果
        </el-button>
      </div>
    </section>

    <section v-else class="empty-card">
      <el-empty description="下载模板或导入 Excel 开始批量转换" />
    </section>

    <!-- 单个生成 -->
    <el-dialog v-model="singleDialogVisible" title="单个生成短链" width="540px" :close-on-click-modal="false" @closed="resetSingleForm">
      <el-form label-width="96px">
        <el-form-item label="短链渠道" required>
          <el-radio-group v-model="singleForm.channel" :disabled="singleGenerating" @change="onSingleChannelChange">
            <el-radio label="jd">京东</el-radio>
            <el-radio label="taobao" :disabled="!taobaoSessionConfigured || taobaoSessionStatus === 'invalid'">淘宝</el-radio>
          </el-radio-group>
          <p v-if="singleForm.channel === 'taobao'" class="form-hint">
            Session：
            <el-tag v-if="taobaoSessionStatus === 'valid'" type="success" size="small">有效</el-tag>
            <el-tag v-else type="danger" size="small">{{ taobaoSessionStatusText }}</el-tag>
          </p>
        </el-form-item>
        <el-form-item label="链接名称">
          <el-input v-model="singleForm.name" :disabled="singleGenerating" />
          <p class="form-hint">
            淘宝名称最多 16 字（含后缀）；提交时自动追加随机字母后缀，如 <code>名称-a3k9</code>
          </p>
        </el-form-item>
        <el-form-item label="长链接">
          <el-input v-model="singleForm.longUrl" type="textarea" :rows="3" :disabled="singleGenerating" @change="onSingleUrlChange" />
        </el-form-item>
        <el-form-item v-if="singleForm.expirationDate" label="过期时间">
          <span>{{ singleForm.expirationDate }}</span>
        </el-form-item>
      </el-form>
      <div v-if="singleForm.result" class="single-result">
        <span class="short-url">{{ singleForm.result }}</span>
        <el-button type="primary" link @click="copyText(singleForm.result)">复制</el-button>
      </div>
      <template #footer>
        <el-button @click="singleDialogVisible = false">关闭</el-button>
        <el-button
          type="primary"
          :loading="singleGenerating"
          :disabled="singleForm.channel === 'taobao' && singleTaobaoBlocked"
          @click="generateSingle"
        >
          生成
        </el-button>
      </template>
    </el-dialog>

    <!-- 多 Sheet 选择 -->
    <el-dialog v-model="sheetDialogVisible" title="选择要生成的 Sheet" width="480px" :close-on-click-modal="false">
      <div class="sheet-dialog-actions">
        <el-button link type="primary" @click="selectAllSheets">全选</el-button>
        <el-button link @click="deselectAllSheets">取消全选</el-button>
      </div>
      <el-checkbox-group v-model="selectedSheetNames" class="sheet-check-group">
        <el-checkbox v-for="name in pendingSheetNames" :key="name" :label="name">{{ name }}</el-checkbox>
      </el-checkbox-group>
      <template #footer>
        <el-button @click="sheetDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!selectedSheetNames.length" @click="confirmSheets">确认导入</el-button>
      </template>
    </el-dialog>

    <!-- 淘宝错误详情 -->
    <el-dialog v-model="taobaoErrorDetailVisible" title="错误详情" width="560px">
      <div v-if="taobaoLastErrorPayload" class="error-detail-body">
        <p v-if="taobaoLastErrorPayload.parsed.outerMsg">
          <strong>msg：</strong>{{ taobaoLastErrorPayload.parsed.outerMsg }}
        </p>
        <p v-if="taobaoLastErrorPayload.parsed.subCode">
          <strong>sub_code：</strong>{{ taobaoLastErrorPayload.parsed.subCode }}
        </p>
        <p v-if="taobaoLastErrorPayload.parsed.subMsg">
          <strong>sub_msg：</strong>{{ taobaoLastErrorPayload.parsed.subMsg }}
        </p>
        <p v-if="taobaoLastErrorPayload.parsed.hint" class="error-detail-hint">
          {{ taobaoLastErrorPayload.parsed.hint.title }} {{ taobaoLastErrorPayload.parsed.hint.hint }}
        </p>
        <pre class="error-detail-json">{{ formatErrorDataJson(taobaoLastErrorPayload.parsed.data) }}</pre>
      </div>
      <template #footer>
        <el-button type="primary" @click="taobaoErrorDetailVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Download, Loading } from '@element-plus/icons-vue'
import * as XLSX from 'xlsx'
import api from '@/utils/http.js'
import {
  SHORT_LINK_DICT_CODE,
  parseShortLinkDict,
  CHANNEL,
  CHANNEL_LABEL,
  detectChannelFromUrl,
  inferColumns,
  isValidUrl,
  calcExpirationDate,
  parseSheetRows,
  parseTaobaoApiError,
  TAOBAO_SESSION_CHECK_URL,
  buildTaobaoRequestName,
  buildJdRequestName,
  buildRandomPrecheckName,
  validateTaobaoDisplayName,
} from './shortLinkUtils.js'

const channel = ref(CHANNEL.JD)
const channelManualOverride = ref(false)
const taobaoSessionKey = ref('')
/** missing | checking | valid | invalid | unknown */
const taobaoSessionStatus = ref('unknown')
const taobaoSessionStatusDetail = ref('')
const taobaoLastErrorPayload = ref(null)
const taobaoErrorDetailVisible = ref(false)
const taobaoSessionConfigured = computed(() => !!taobaoSessionKey.value)
const taobaoCanGenerate = computed(() => taobaoSessionStatus.value === 'valid')
const pageTaobaoBlocked = computed(
  () => channel.value === CHANNEL.TAOBAO && !taobaoCanGenerate.value,
)
const singleTaobaoBlocked = computed(
  () => singleForm.channel === CHANNEL.TAOBAO && !taobaoCanGenerate.value,
)
const taobaoSessionStatusText = computed(() => {
  const map = {
    missing: '未配置',
    checking: '检测中',
    valid: '有效',
    invalid: '无效',
    unknown: '未检测',
    idle: '—',
  }
  return map[taobaoSessionStatus.value] || '未知'
})

/** 右上角 Session 标签：固定文案宽度，避免检测时闪烁 */
const sessionTagLabel = computed(() => {
  if (channel.value !== CHANNEL.TAOBAO) return '—'
  return taobaoSessionStatusText.value
})

const sessionTagType = computed(() => {
  if (channel.value !== CHANNEL.TAOBAO) return 'info'
  const map = {
    checking: 'info',
    valid: 'success',
    invalid: 'danger',
    missing: 'warning',
    unknown: 'info',
  }
  return map[taobaoSessionStatus.value] || 'info'
})

const previewDataMap = reactive({})
const rawSheetJson = reactive({})
const sheetHeaders = reactive({})
const columnMap = reactive({})
const currentPreviewSheet = ref('')
const validationErrors = ref([])
const hasGeneratedLinks = ref(false)
const isGenerating = ref(false)
const progressDone = ref(0)
const progressTotal = ref(0)
const progressMessage = ref('')

const sheetDialogVisible = ref(false)
const pendingSheetNames = ref([])
const selectedSheetNames = ref([])
const pendingWorkbook = ref(null)

const singleDialogVisible = ref(false)
const singleGenerating = ref(false)
const singleForm = reactive({
  channel: CHANNEL.JD,
  name: '',
  longUrl: '',
  result: '',
  expirationDate: '',
})

const activeSheetNames = computed(() => Object.keys(previewDataMap))
const totalRows = computed(() =>
  Object.values(previewDataMap).reduce((n, rows) => n + (rows?.length || 0), 0),
)
const progressPercent = computed(() => {
  if (!progressTotal.value) return 0
  return Math.min(100, Math.round((progressDone.value / progressTotal.value) * 100))
})

onMounted(async () => {
  await loadTaobaoSession()
  if (!taobaoSessionConfigured.value) {
    taobaoSessionStatus.value = 'missing'
    taobaoSessionStatusDetail.value = '未配置淘宝 SessionKey，请联系管理员'
  }
})

watch(channel, () => {
  validateAll()
})

async function loadTaobaoSession() {
  try {
    const raw = await api.getDictByCode(SHORT_LINK_DICT_CODE)
    const { sessionKey } = parseShortLinkDict(raw)
    taobaoSessionKey.value = sessionKey
    if (!sessionKey) {
      taobaoSessionStatus.value = 'missing'
      taobaoSessionStatusDetail.value = '淘宝 session 获取失败，请联系管理员处理'
    }
  } catch (e) {
    console.error(e)
    taobaoSessionStatus.value = 'missing'
    taobaoSessionStatusDetail.value = '淘宝 session 获取失败，请联系管理员处理'
  }
}

function formatErrorDataJson(data) {
  if (!data || typeof data !== 'object') return '{}'
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return String(data)
  }
}

function openTaobaoErrorDetail() {
  if (taobaoLastErrorPayload.value) taobaoErrorDetailVisible.value = true
}

function notifyTaobaoError(res) {
  const parsed = parseTaobaoApiError(res)
  taobaoLastErrorPayload.value = { res, parsed }
  taobaoSessionStatusDetail.value = parsed.summary
  ElMessage({
    type: 'error',
    message: parsed.summary,
    duration: 0,
    showClose: true,
  })
  return parsed
}

async function runTaobaoPrecheck() {
  if (!taobaoSessionKey.value) {
    taobaoSessionStatus.value = 'missing'
    taobaoSessionStatusDetail.value = '未配置淘宝 SessionKey，请联系管理员'
    ElMessage({
      type: 'error',
      message: taobaoSessionStatusDetail.value,
      duration: 0,
      showClose: true,
    })
    return false
  }

  taobaoSessionStatus.value = 'checking'
  const precheckName = buildRandomPrecheckName()

  try {
    const res = await api.post('/tb/api/v1/generate-short-link', {
      short_link_name: precheckName,
      short_link_data: TAOBAO_SESSION_CHECK_URL,
      session: taobaoSessionKey.value,
    })

    if (res.code === 200 && res.msg === 'Success' && res.data?.short_link) {
      taobaoSessionStatus.value = 'valid'
      taobaoSessionStatusDetail.value = 'SessionKey 校验通过'
      taobaoLastErrorPayload.value = null
      return true
    }

    taobaoSessionStatus.value = 'invalid'
    notifyTaobaoError(res)
    return false
  } catch (e) {
    console.error(e)
    taobaoSessionStatus.value = 'invalid'
    taobaoSessionStatusDetail.value = '预检查请求失败，请稍后重试'
    ElMessage({
      type: 'error',
      message: taobaoSessionStatusDetail.value,
      duration: 0,
      showClose: true,
    })
    return false
  }
}

async function onChannelManualChange() {
  channelManualOverride.value = true
  if (channel.value !== CHANNEL.TAOBAO) return

  if (!taobaoSessionConfigured.value) {
    taobaoSessionStatus.value = 'missing'
    ElMessage({
      type: 'error',
      message: '淘宝 session 获取失败，请联系管理员处理',
      duration: 0,
      showClose: true,
    })
    channel.value = CHANNEL.JD
    return
  }
  await runTaobaoPrecheck()
}

async function tryAutoSwitchChannel(url, sourceLabel) {
  if (channelManualOverride.value) return
  const detected = detectChannelFromUrl(url)
  if (!detected || detected === channel.value) return
  if (detected === CHANNEL.TAOBAO && !taobaoSessionConfigured.value) return
  channel.value = detected
  if (singleDialogVisible.value) singleForm.channel = detected
  ElMessage.info(`已根据链接自动切换为「${CHANNEL_LABEL[detected]}」渠道（${sourceLabel}）`)
  if (detected === CHANNEL.TAOBAO) await runTaobaoPrecheck()
}

function onLongUrlChange(row) {
  tryAutoSwitchChannel(row.longUrl, '表格链接')
  validateAll()
}

function onSingleUrlChange() {
  tryAutoSwitchChannel(singleForm.longUrl, '单个生成')
}

function openSingleDialog() {
  singleForm.channel = channel.value
  singleDialogVisible.value = true
}

async function onSingleChannelChange() {
  channelManualOverride.value = true
  channel.value = singleForm.channel
  if (singleForm.channel === CHANNEL.TAOBAO && taobaoSessionConfigured.value) {
    await runTaobaoPrecheck()
  }
}

function resetSingleForm() {
  singleForm.channel = channel.value
  singleForm.name = ''
  singleForm.longUrl = ''
  singleForm.result = ''
  singleForm.expirationDate = ''
}

function downloadTemplate() {
  const data = [{ 链接名称: '示例名称', 长链接: 'https://example.com/path' }]
  const ws = XLSX.utils.json_to_sheet(data)
  ws['!cols'] = [{ wch: 20 }, { wch: 50 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, '模板')
  XLSX.writeFile(wb, 'short_link_template.xlsx')
  ElMessage.success('模板已下载')
}

function handleFileUpload(uploadFile) {
  const file = uploadFile?.raw
  if (!file) return
  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' })
      channelManualOverride.value = false
      if (wb.SheetNames.length > 1) {
        pendingWorkbook.value = wb
        pendingSheetNames.value = [...wb.SheetNames]
        selectedSheetNames.value = [...wb.SheetNames]
        sheetDialogVisible.value = true
      } else {
        applyWorkbook(wb, wb.SheetNames)
      }
    } catch (err) {
      console.error(err)
      ElMessage.error('Excel 读取失败')
    }
  }
  reader.readAsArrayBuffer(file)
}

function selectAllSheets() {
  selectedSheetNames.value = [...pendingSheetNames.value]
}

function deselectAllSheets() {
  selectedSheetNames.value = []
}

function confirmSheets() {
  if (!selectedSheetNames.value.length) {
    ElMessage.warning('请至少选择一个 Sheet')
    return
  }
  applyWorkbook(pendingWorkbook.value, selectedSheetNames.value)
  sheetDialogVisible.value = false
  pendingWorkbook.value = null
}

function applyWorkbook(workbook, sheetNameList) {
  Object.keys(previewDataMap).forEach((k) => delete previewDataMap[k])
  Object.keys(rawSheetJson).forEach((k) => delete rawSheetJson[k])
  Object.keys(sheetHeaders).forEach((k) => delete sheetHeaders[k])
  Object.keys(columnMap).forEach((k) => delete columnMap[k])
  hasGeneratedLinks.value = false

  let total = 0
  let firstUrlForChannel = ''

  sheetNameList.forEach((sheetName) => {
    const ws = workbook.Sheets[sheetName]
    const json = XLSX.utils.sheet_to_json(ws)
    if (!json.length) return

    const headers = []
    const set = new Set()
    json.forEach((row) => Object.keys(row).forEach((k) => set.add(k)))
    set.forEach((h) => headers.push(h))

    const { nameKey, urlKey } = inferColumns(headers, json.slice(0, 20))
    rawSheetJson[sheetName] = json
    sheetHeaders[sheetName] = headers
    columnMap[sheetName] = { nameKey, urlKey }
    previewDataMap[sheetName] = parseSheetRows(json, nameKey, urlKey)
    total += previewDataMap[sheetName].length
    const sampleUrl = previewDataMap[sheetName].find((r) => r.longUrl)?.longUrl
    if (sampleUrl && !firstUrlForChannel) firstUrlForChannel = sampleUrl
  })

  const names = Object.keys(previewDataMap)
  if (names.length) currentPreviewSheet.value = names[0]
  validateAll()

  if (firstUrlForChannel) tryAutoSwitchChannel(firstUrlForChannel, '导入数据')

  if (!total) ElMessage.warning('未解析到有效数据')
  else ElMessage.success(`已导入 ${total} 条（${names.length} 个 Sheet）`)
}

function rebuildCurrentSheet() {
  const sheet = currentPreviewSheet.value
  const map = columnMap[sheet]
  if (!map || !rawSheetJson[sheet]) return
  previewDataMap[sheet] = parseSheetRows(rawSheetJson[sheet], map.nameKey, map.urlKey)
  validateAll()
}

function validateAll() {
  const errors = []
  const checkTaobaoName = channel.value === CHANNEL.TAOBAO
  Object.entries(previewDataMap).forEach(([sheet, rows]) => {
    rows.forEach((row, i) => {
      if (!row.name?.trim()) errors.push(`[${sheet}] 第 ${i + 1} 行：名称不能为空`)
      else if (checkTaobaoName) {
        const nameErr = validateTaobaoDisplayName(row.name)
        if (nameErr) errors.push(`[${sheet}] 第 ${i + 1} 行：${nameErr}`)
      }
      if (!row.longUrl?.trim()) errors.push(`[${sheet}] 第 ${i + 1} 行：链接不能为空`)
      else if (!isValidUrl(row.longUrl)) errors.push(`[${sheet}] 第 ${i + 1} 行：链接需以 http(s) 开头`)
    })
  })
  validationErrors.value = errors
}

function removeRow(index) {
  previewDataMap[currentPreviewSheet.value].splice(index, 1)
  validateAll()
}

async function generateJdSingle(displayName, longUrl, seq = 0) {
  const requestName = buildJdRequestName(displayName, seq)
  const res = await api.post('/chanel-yp-api/yp-api-service/api/v1/shot/link/create', {
    name: requestName,
    long_url: longUrl.trim(),
  })
  if (res.code === 200 && res.data?.short_url) return res.data.short_url
  throw new Error(res.message || '京东短链生成失败')
}

async function generateTaobaoSingle(displayName, longUrl, seq = 0) {
  if (!taobaoSessionKey.value) {
    const err = new Error('淘宝 session 获取失败，请联系管理员处理')
    err.isSessionInvalid = true
    throw err
  }
  if (taobaoSessionStatus.value !== 'valid') {
    const err = new Error(taobaoSessionStatusDetail.value || '淘宝 SessionKey 无效')
    err.isSessionInvalid = true
    throw err
  }
  const nameErr = validateTaobaoDisplayName(displayName)
  if (nameErr) throw new Error(nameErr)

  const requestName = buildTaobaoRequestName(displayName, seq)
  const res = await api.post('/tb/api/v1/generate-short-link', {
    short_link_name: requestName,
    short_link_data: longUrl.trim(),
    session: taobaoSessionKey.value,
  })
  if (res.code === 200 && res.msg === 'Success' && res.data?.short_link) return res.data.short_link
  const parsed = parseTaobaoApiError(res)
  const err = new Error(parsed.summary)
  err.taobaoRes = res
  err.parsed = parsed
  err.isSessionInvalid = parsed.isSessionInvalid
  throw err
}

async function generateSingle() {
  if (!singleForm.name?.trim()) return ElMessage.error('请输入链接名称')
  if (!singleForm.longUrl?.trim() || !isValidUrl(singleForm.longUrl)) return ElMessage.error('请输入有效长链接')
  if (singleForm.channel === CHANNEL.TAOBAO) {
    if (!taobaoSessionConfigured.value) {
      return ElMessage({
        type: 'error',
        message: '淘宝 session 获取失败，请联系管理员处理',
        duration: 0,
        showClose: true,
      })
    }
    if (taobaoSessionStatus.value !== 'valid') {
      return ElMessage({
        type: 'error',
        message: taobaoSessionStatusDetail.value || '淘宝 SessionKey 无效，请重新检测',
        duration: 0,
        showClose: true,
      })
    }
    const nameErr = validateTaobaoDisplayName(singleForm.name)
    if (nameErr) return ElMessage.error(nameErr)
  }

  singleGenerating.value = true
  singleForm.result = ''
  try {
    const short =
      singleForm.channel === CHANNEL.JD
        ? await generateJdSingle(singleForm.name, singleForm.longUrl)
        : await generateTaobaoSingle(singleForm.name, singleForm.longUrl)
    singleForm.result = short
    singleForm.expirationDate = calcExpirationDate()
    ElMessage.success('生成成功')
  } catch (e) {
    if (e.taobaoRes) {
      notifyTaobaoError(e.taobaoRes)
      if (e.isSessionInvalid) taobaoSessionStatus.value = 'invalid'
    } else {
      ElMessage({
        type: 'error',
        message: e.message || '生成失败',
        duration: 0,
        showClose: true,
      })
    }
  } finally {
    singleGenerating.value = false
  }
}

function setProgress(done, total, msg) {
  progressDone.value = done
  progressTotal.value = total
  progressMessage.value = msg
}

async function batchGenerate() {
  if (validationErrors.value.length) return ElMessage.error('请先修正校验错误')
  if (!totalRows.value) return ElMessage.error('没有可生成的数据')
  if (channel.value === CHANNEL.TAOBAO) {
    if (!taobaoSessionConfigured.value) {
      return ElMessage.error('淘宝 session 获取失败，请联系管理员处理')
    }
    if (taobaoSessionStatus.value !== 'valid') {
      return ElMessage.error(taobaoSessionStatusDetail.value || '淘宝 SessionKey 无效，请重新检测')
    }
  }

  const channelLabel = CHANNEL_LABEL[channel.value]
  try {
    await ElMessageBox.confirm(
      `即将使用【${channelLabel}】渠道批量生成 ${totalRows.value} 条短链。\n\n` +
        `提交时每条链接名称将自动追加随机字母后缀以保证唯一。\n\n` +
        `请再次确认当前渠道选择正确。`,
      '确认生成渠道',
      {
        type: 'warning',
        confirmButtonText: `确认使用${channelLabel}`,
        cancelButtonText: '取消',
      },
    )
  } catch {
    return
  }

  isGenerating.value = true
  hasGeneratedLinks.value = false
  const allTasks = []
  Object.entries(previewDataMap).forEach(([sheet, rows]) => {
    rows.forEach((row, idx) => {
      if (row.status !== '已生成') allTasks.push({ sheet, row, idx })
    })
  })

  setProgress(0, allTasks.length, '准备生成…')

  try {
    if (channel.value === CHANNEL.JD) {
      await batchGenerateJd(allTasks)
    } else {
      await batchGenerateTaobao(allTasks)
    }
    hasGeneratedLinks.value = true
    ElMessage.success('批量生成完成')
  } catch (e) {
    console.error(e)
    ElMessage.error(e.message || '批量生成失败')
  } finally {
    isGenerating.value = false
    setProgress(0, 0, '')
  }
}

async function batchGenerateJd(tasks) {
  const bySheet = {}
  tasks.forEach((t) => {
    if (!bySheet[t.sheet]) bySheet[t.sheet] = []
    bySheet[t.sheet].push(t)
  })

  let done = 0
  for (const [sheet, sheetTasks] of Object.entries(bySheet)) {
    const payload = sheetTasks.map((t, i) => ({
      name: buildJdRequestName(t.row.name.trim(), i + 1),
      long_url: t.row.longUrl.trim(),
    }))
    setProgress(done, tasks.length, `正在生成 [${sheet}]…`)
    const res = await api.post('/chanel-yp-api/yp-api-service/api/v1/shot/link/create/batch', payload)
    if (res.code !== 200 || !res.data) throw new Error(`[${sheet}] 京东批量生成失败`)
    const exp = calcExpirationDate()
    res.data.forEach((item, i) => {
      const t = sheetTasks[i]
      if (t) {
        t.row.shortUrl = item.short_url || ''
        t.row.status = t.row.shortUrl ? '已生成' : '失败'
        t.row.expirationDate = exp
      }
      done += 1
      setProgress(done, tasks.length, `已完成 [${sheet}] ${i + 1}/${sheetTasks.length}`)
    })
  }
}

async function batchGenerateTaobao(tasks) {
  let done = 0
  const exp = calcExpirationDate()
  for (const { sheet, row, idx } of tasks) {
    setProgress(done, tasks.length, `正在生成 [${sheet}] 第 ${idx + 1} 条…`)
    row.status = '生成中'
    try {
      const short = await generateTaobaoSingle(row.name, row.longUrl)
      row.shortUrl = short
      row.status = '已生成'
      row.expirationDate = exp
    } catch (e) {
      row.status = '失败'
      if (e.taobaoRes) {
        notifyTaobaoError(e.taobaoRes)
        if (e.isSessionInvalid) {
          taobaoSessionStatus.value = 'invalid'
          break
        }
      } else {
        ElMessage({
          type: 'error',
          message: e.message || '生成失败',
          duration: 0,
          showClose: true,
        })
      }
    }
    done += 1
    setProgress(done, tasks.length, `进度 ${done}/${tasks.length}`)
  }
}

function downloadResult() {
  const wb = XLSX.utils.book_new()
  Object.entries(previewDataMap).forEach(([sheetName, rows]) => {
    const data = rows.map((r) => ({
      链接名称: r.name,
      长链接: r.longUrl,
      短链接: r.shortUrl || '',
      过期时间: r.expirationDate || '',
      渠道: CHANNEL_LABEL[channel.value],
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    ws['!cols'] = [{ wch: 18 }, { wch: 48 }, { wch: 28 }, { wch: 20 }, { wch: 8 }]
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31))
  })
  XLSX.writeFile(wb, `short_link_result_${Date.now()}.xlsx`)
  ElMessage.success('结果已下载')
}

function copyText(text) {
  navigator.clipboard.writeText(text).then(
    () => ElMessage.success('已复制'),
    () => ElMessage.error('复制失败'),
  )
}
</script>

<style scoped>
.short-link-tool {
  min-height: 100%;
  padding: 24px;
  background: linear-gradient(160deg, #f0f4f8 0%, #e8eef5 50%, #f5f7fa 100%);
}

.tool-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.tool-header__title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
}

.tool-header__desc {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.channel-panel {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 300px;
  min-height: 152px;
  padding: 16px 20px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.channel-panel--taobao {
  border-color: #ff6a00;
  box-shadow: 0 4px 16px rgba(255, 106, 0, 0.12);
}

.channel-panel--jd {
  border-color: #e1251b;
  box-shadow: 0 4px 16px rgba(225, 37, 27, 0.1);
}

.channel-panel__label {
  font-size: 14px;
  font-weight: 700;
  color: #b45309;
  margin-bottom: 12px;
}

.channel-panel--taobao .channel-panel__label {
  color: #c2410c;
}

.channel-panel--jd .channel-panel__label {
  color: #b91c1c;
}

.channel-panel__required {
  color: #ef4444;
  margin-right: 2px;
}

.channel-panel__radios {
  display: flex;
  width: 100%;
}

.channel-panel__radios :deep(.el-radio-button) {
  flex: 1;
}

.channel-panel__radios :deep(.el-radio-button__inner) {
  width: 100%;
  font-weight: 600;
}

.channel-panel__status {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  min-height: 32px;
  border-top: 1px dashed #e2e8f0;
  font-size: 13px;
}

.channel-panel--taobao .channel-panel__status {
  border-top-color: #fed7aa;
}

.status-label {
  flex-shrink: 0;
  width: 82px;
  color: #64748b;
  font-weight: 500;
}

.session-status-tag {
  flex-shrink: 0;
  min-width: 56px;
  text-align: center;
  justify-content: center;
}

.session-status-tag :deep(.el-tag__content) {
  min-width: 40px;
  text-align: center;
}

.status-action {
  flex: 1;
  min-width: 64px;
  text-align: right;
}

.status-recheck-btn {
  min-width: 56px;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.form-hint code {
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 4px;
  border-radius: 3px;
}

.short-link-tool--locked {
  overflow: hidden;
  height: 100%;
}

.batch-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
}

.batch-overlay__panel {
  width: min(440px, 92vw);
  padding: 32px 28px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.batch-overlay__icon {
  font-size: 40px;
  color: #3b82f6;
  margin-bottom: 12px;
}

.batch-overlay__title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.batch-overlay__channel {
  margin: 0 0 12px;
  font-size: 14px;
  color: #475569;
}

.batch-overlay__channel strong {
  color: #dc2626;
  font-size: 16px;
}

.batch-overlay__msg {
  margin: 0 0 16px;
  font-size: 13px;
  color: #64748b;
}

.batch-overlay__count {
  margin: 12px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #334155;
}

.batch-overlay__tip {
  margin: 16px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.session-alert {
  margin-bottom: 16px;
}

.toolbar-card,
.mapping-card,
.preview-card,
.empty-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.toolbar-hint {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.mapping-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.mapping-label {
  font-size: 13px;
  color: #475569;
}

.preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.preview-head h3 {
  margin: 0;
  font-size: 16px;
  color: #1e293b;
}

.row-count {
  font-size: 13px;
  color: #64748b;
}

.sheet-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.sheet-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.sheet-tab.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.sheet-tab__count {
  font-size: 11px;
  opacity: 0.85;
}

.short-url {
  color: #2563eb;
  word-break: break-all;
  font-size: 12px;
}

.status-muted {
  color: #94a3b8;
  font-size: 12px;
}

.error-alert {
  margin-top: 16px;
}

.error-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
}

.preview-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.single-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #eff6ff;
  border-radius: 8px;
  margin-top: 8px;
}

.sheet-dialog-actions {
  margin-bottom: 12px;
}

.sheet-check-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.error-detail-btn {
  margin-left: 12px;
  vertical-align: baseline;
}

.error-detail-body p {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.5;
}

.error-detail-hint {
  color: #b45309;
  background: #fffbeb;
  padding: 8px 12px;
  border-radius: 6px;
}

.error-detail-json {
  margin: 12px 0 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  overflow: auto;
  max-height: 320px;
}
</style>
